import { useState, useCallback, useMemo } from 'react';
import { notification } from 'antd';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { openEvaluation, updateChatAssistantDraft } from '../../../API/services/screeningService';
import { t } from '../../../i18n/i18n';
import {
  CHAT_ASSISTANT_DEFAULTS,
  EVALUATION_DEFAULTS,
} from '../../../constants/chatAssistant.constants';
import { TestGroupTypes } from '../../../constants/enums';

export const useEvaluationManagement = (addPendingSavePromise) => {
  const [percentage, setPercentage] = useState(CHAT_ASSISTANT_DEFAULTS.PROGRESS_PERCENTAGE_INITIAL);
  const [isCreatingEvaluation, setIsCreatingEvaluation] = useState(false);
  const [evaluationData, setEvaluationData] = useState({
    subskills: [],
    difficultyConfig: null,
    customTestGroup: null,
    evaluationMetadata: null,
  });

  const { currentWorkspace, currentCompany, user } = useSelector((state) => state.auth);
  const history = useHistory();

  const checkIfEvaluationReady = useCallback((evalData, percent) => {
    if (evalData?.result?.success === true) {
      return true;
    }

    const hasTests = evalData?.customTestGroup?.tests?.length > 0;
    const isComplete = percent === CHAT_ASSISTANT_DEFAULTS.PROGRESS_PERCENTAGE_COMPLETE;
    return isComplete && hasTests;
  }, []);

  const isEvaluationReady = useMemo(() => {
    return checkIfEvaluationReady(evaluationData, percentage);
  }, [evaluationData, percentage, checkIfEvaluationReady]);

  const updateEvaluationState = useCallback(
    async (extractedData, draftId, shouldSaveToBackend = true) => {
      const {
        subskills,
        difficultyConfig,
        customTestGroup,
        evaluationMetadata,
        progressPercentage,
      } = extractedData;

      let hasChanges = false;
      const updateData = {};

      if (subskills.length > 0 || difficultyConfig || customTestGroup || evaluationMetadata) {
        setEvaluationData((prevData) => {
          if (customTestGroup && customTestGroup !== prevData.customTestGroup) {
            updateData.customTestGroup = customTestGroup;
            hasChanges = true;
          }

          if (subskills.length > 0 && subskills !== prevData.subskills) {
            updateData.subskills = subskills;
            hasChanges = true;
          }

          if (evaluationMetadata && evaluationMetadata !== prevData.evaluationMetadata) {
            updateData.evaluationMetadata = evaluationMetadata;
            hasChanges = true;
          }

          return {
            ...prevData,
            subskills: subskills.length > 0 ? subskills : prevData.subskills,
            difficultyConfig: difficultyConfig || prevData.difficultyConfig,
            customTestGroup: customTestGroup || prevData.customTestGroup,
            evaluationMetadata: evaluationMetadata || prevData.evaluationMetadata,
          };
        });

        if (progressPercentage !== undefined && progressPercentage !== null) {
          updateData.percentage = progressPercentage;
          hasChanges = true;
        }

        if (hasChanges && draftId && shouldSaveToBackend) {
          try {
            const savePromise = updateChatAssistantDraft(draftId, updateData);
            if (addPendingSavePromise) {
              addPendingSavePromise(savePromise);
            }
            await savePromise;
          } catch (error) {
            console.error('Error auto-saving evaluation data:', error);
            throw error;
          }
        }

        if (
          shouldSaveToBackend &&
          (subskills.length > 0 || customTestGroup || evaluationMetadata)
        ) {
          notification.success({
            message: t('CHAT_AUTO_SAVED'),
            description: t('CHAT_CHANGES_SAVED_AUTOMATICALLY'),
            duration: 1.5,
          });
        }
      }

      if (progressPercentage !== undefined && progressPercentage !== null) {
        setPercentage(progressPercentage);
      }
    },
    [addPendingSavePromise]
  );

  const formatMessagesForDisplay = useCallback((messages) => {
    if (!messages || messages.length === 0) return [];

    return messages.map((msg, index) => ({
      id: msg.id || `msg_${Date.now()}_${index}`,
      role: msg.role,
      content: msg.content,
    }));
  }, []);

  const handleUpdateEvaluation = useCallback(
    async (updatedData, draftId) => {
      setEvaluationData(updatedData);

      if (draftId) {
        const updateData = {};

        if (updatedData.proctoringEnabled !== undefined) {
          updateData.proctoringEnabled = updatedData.proctoringEnabled;
        }
        if (updatedData.proctoringAIEnabled !== undefined) {
          updateData.proctoringAIEnabled = updatedData.proctoringAIEnabled;
        }
        if (updatedData.allowCertificateDownload !== undefined) {
          updateData.allowCertificateDownload = updatedData.allowCertificateDownload;
        }
        if (updatedData.requireCompletedProfile !== undefined) {
          updateData.requireCompletedProfile = updatedData.requireCompletedProfile;
        }
        if (updatedData.emptyDates !== undefined) {
          updateData.emptyDates = updatedData.emptyDates;
        }
        if (updatedData.startDate !== undefined) {
          updateData.startDate = updatedData.startDate;
        }
        if (updatedData.endDate !== undefined) {
          updateData.endDate = updatedData.endDate;
        }
        if (updatedData.allowAnonymousAccess !== undefined) {
          updateData.allowAnonymousAccess = updatedData.allowAnonymousAccess;
        }
        if (updatedData.exclusiveAccess !== undefined) {
          updateData.exclusiveAccess = updatedData.exclusiveAccess;
        }
        if (updatedData.enableReminders !== undefined) {
          updateData.enableReminders = updatedData.enableReminders;
        }

        try {
          await updateChatAssistantDraft(draftId, updateData);
        } catch (error) {
          console.error('Error updating draft config:', error);
        }
      }

      notification.success({
        message: t('CHAT_AUTO_SAVED'),
        description: t('CHAT_CONFIG_UPDATED_AUTOMATICALLY'),
        duration: 1.5,
      });
    },
    [setEvaluationData]
  );

  const handleCreateEvaluation = useCallback(
    async (draftData, draftId) => {
      if (isCreatingEvaluation) {
        return;
      }

      if (!draftId) {
        notification.error({
          message: t('ERROR'),
          description: t('CHAT_NO_DRAFT_ID'),
        });
        return;
      }

      const customTestGroup = draftData?.customTestGroup || evaluationData.customTestGroup;

      if (!customTestGroup) {
        notification.error({
          message: t('ERROR'),
          description: t('CHAT_NO_EVALUATION_DATA'),
        });
        return;
      }

      const hasTests = customTestGroup?.tests?.length > 0;
      if (!hasTests) {
        notification.error({
          message: t('ERROR'),
          description: t('CHAT_NO_TESTS_CONFIGURED'),
        });
        return;
      }

      const hasDifficultyConfig = customTestGroup?.difficultyConfig?.length > 0;
      if (!hasDifficultyConfig) {
        notification.error({
          message: t('ERROR'),
          description: t('CHAT_NO_DIFFICULTY_CONFIG'),
        });
        return;
      }

      setIsCreatingEvaluation(true);

      try {
        const formattedCustomTestGroup = {
          type: TestGroupTypes.CUSTOM,
          name: draftData.evaluationMetadata?.name || customTestGroup.name || draftData.name,
          weight: EVALUATION_DEFAULTS.DEFAULT_WEIGHT,
          order: EVALUATION_DEFAULTS.DEFAULT_ORDER,
          maxScore: EVALUATION_DEFAULTS.DEFAULT_MAX_SCORE,
          duration:
            draftData.evaluationMetadata?.estimatedTime ||
            customTestGroup.duration ||
            draftData.duration ||
            EVALUATION_DEFAULTS.DEFAULT_DURATION,
          hasSkipQuestion: EVALUATION_DEFAULTS.HAS_SKIP_QUESTION,
          tests: (customTestGroup.tests || []).map((testId) =>
            typeof testId === 'string' ? { testId } : testId
          ),
          difficultyConfig: customTestGroup.difficultyConfig || [],
          languages: customTestGroup.languages || [
            draftData.language || CHAT_ASSISTANT_DEFAULTS.LANGUAGE,
          ],
          enabled: EVALUATION_DEFAULTS.ENABLED,
          isAutomaticContinue: EVALUATION_DEFAULTS.IS_AUTOMATIC_CONTINUE,
          withoutTime: customTestGroup.withoutTime ?? EVALUATION_DEFAULTS.WITHOUT_TIME,
          isAIAssistant: true,
          recommendedDevices: ['LAPTOP', 'IPAD', 'MOBILE'],
          withCustomLangCodesAI: customTestGroup.withCustomLangCodesAI ?? true,
          preLangCodesAvailable: customTestGroup.preLangCodesAvailable || [],
        };

        const dataToPost = {
          id: draftId,
          name:
            draftData.evaluationMetadata?.name || draftData.name || t('CHAT_TECHNICAL_EVALUATION'),
          language: draftData.language || CHAT_ASSISTANT_DEFAULTS.LANGUAGE,
          companyId: currentCompany?.id || user?.companyId,
          userId: user?.id,
          duration:
            draftData.evaluationMetadata?.estimatedTime ||
            draftData.duration ||
            customTestGroup?.duration ||
            EVALUATION_DEFAULTS.DEFAULT_DURATION,
          startDate: draftData.startDate || new Date().toISOString(),
          endDate:
            draftData.endDate ||
            new Date(
              Date.now() +
                EVALUATION_DEFAULTS.DAYS_TO_ADD * EVALUATION_DEFAULTS.MILLISECONDS_PER_DAY
            ).toISOString(),
          workspaceId: currentWorkspace,
          purpose: draftData.purpose || EVALUATION_DEFAULTS.DEFAULT_PURPOSE,
          allowCertificateDownload:
            evaluationData.allowCertificateDownload ??
            CHAT_ASSISTANT_DEFAULTS.ALLOW_CERTIFICATE_DOWNLOAD,
          proctoringEnabled:
            evaluationData.proctoringEnabled ?? CHAT_ASSISTANT_DEFAULTS.PROCTORING_ENABLED,
          proctoringAIEnabled:
            evaluationData.proctoringAIEnabled ?? CHAT_ASSISTANT_DEFAULTS.PROCTORING_AI_ENABLED,
          requireCompletedProfile:
            evaluationData.requireCompletedProfile ??
            CHAT_ASSISTANT_DEFAULTS.REQUIRE_COMPLETED_PROFILE,
          reminderToCandidates: evaluationData.enableReminders ?? true,
          exclusiveUsers: evaluationData.exclusiveAccess ?? false,
          accessWithoutAccount: evaluationData.allowAnonymousAccess ?? false,
          emptyDates: evaluationData.enableScheduleDates === false,
          reviewType: EVALUATION_DEFAULTS.DEFAULT_REVIEW_TYPE,
          testGroups: [formattedCustomTestGroup],
          keepDraft: true,
        };

        const result = await openEvaluation(dataToPost);

        if (result?.result?.id || result?.id) {
          const evaluationId = result.result?.id || result.id;

          notification.success({
            message: t('SEARCH_CREATED_SUCCESSFULLY'),
          });

          setTimeout(() => {
            history.push(`/search/${evaluationId}?workspaceId=${currentWorkspace}`);
          }, CHAT_ASSISTANT_DEFAULTS.EVALUATION_REDIRECT_DELAY);
        } else {
          throw new Error(t('CHAT_ERROR_NO_EVALUATION_ID'));
        }
      } catch (error) {
        notification.error({
          message: t('SEARCH_CREATE_ERROR'),
          description: error.response?.data?.message || t('CHAT_ERROR_CREATE_EVALUATION'),
        });
        setIsCreatingEvaluation(false);
      }
    },
    [isCreatingEvaluation, evaluationData, currentCompany, user, currentWorkspace, history]
  );

  const resetEvaluationData = useCallback(() => {
    setEvaluationData({
      subskills: [],
      difficultyConfig: null,
      customTestGroup: null,
      evaluationMetadata: null,
    });
    setPercentage(CHAT_ASSISTANT_DEFAULTS.PROGRESS_PERCENTAGE_INITIAL);
  }, []);

  const loadEvaluationFromDraft = useCallback((draftData) => {
    const {
      customTestGroup,
      percentage: draftPercentage,
      subskills,
      evaluationMetadata,
    } = draftData;

    if (draftPercentage !== undefined && draftPercentage !== null) {
      setPercentage(draftPercentage);
    }

    if (customTestGroup || subskills || evaluationMetadata) {
      setEvaluationData((prevData) => ({
        ...prevData,
        customTestGroup: customTestGroup || prevData.customTestGroup,
        difficultyConfig: customTestGroup?.difficultyConfig || prevData.difficultyConfig,
        subskills: subskills || customTestGroup?.subskills || prevData.subskills,
        evaluationMetadata: evaluationMetadata || prevData.evaluationMetadata,
      }));
    }
  }, []);

  return {
    evaluationData,
    percentage,
    isCreatingEvaluation,
    isEvaluationReady,
    setEvaluationData,
    setPercentage,
    updateEvaluationState,
    formatMessagesForDisplay,
    handleUpdateEvaluation,
    handleCreateEvaluation,
    resetEvaluationData,
    loadEvaluationFromDraft,
  };
};
